import React, { useState, useEffect, useRef, useCallback } from "react";
import { RealtimeAgent, RealtimeSession, tool } from "@openai/agents-realtime";
import { z } from "zod";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import styles from "./VoiceMode.module.css";

// Custom hook to manage state that needs to be accessed in animation loops
function useStateWithRef<T>(initialValue: T) {
  const [state, setState] = useState<T>(initialValue);
  const ref = useRef<T>(initialValue);

  const setStateAndRef = useCallback((value: T | ((prev: T) => T)) => {
    setState((prevState) => {
      const newValue =
        typeof value === "function"
          ? (value as (prev: T) => T)(prevState)
          : value;
      ref.current = newValue;
      return newValue;
    });
  }, []);

  return [state, setStateAndRef, ref] as const;
}

interface VoiceModeProps {
  onClose: () => void;
  selectedPersona: "neutral" | "donna";
  selectedVoice: string;
  threadId?: string;
  isPublic?: boolean;
  onUserTranscriptionDelta?: (text: string) => void;
  onUserTranscriptionComplete?: (text: string) => void;
  onAIResponseDelta?: (text: string) => void;
  onAIResponseComplete?: (text: string) => void;
}

interface EphemeralTokenResponse {
  success?: boolean;
  token: string;
  error?: string;
}

const VoiceMode: React.FC<VoiceModeProps> = ({
  onClose,
  selectedPersona,
  selectedVoice,
  threadId = "",
  isPublic = false,
  onUserTranscriptionDelta,
  onUserTranscriptionComplete,
  onAIResponseDelta,
  onAIResponseComplete,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected, isConnectedRef] = useStateWithRef(false);
  const [userIsSpeaking, setUserIsSpeaking, userIsSpeakingRef] =
    useStateWithRef(false);
  const [aiIsSpeaking, setAIIsSpeaking, aiIsSpeakingRef] =
    useStateWithRef(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] =
    useState<string>("Initializing...");

  const sessionRef = useRef<RealtimeSession | null>(null);
  const userTranscriptionRef = useRef<string>("");
  const aiResponseRef = useRef<string>("");
  const currentUserMessageRef = useRef<string>(""); // Store the current user message for API call
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const responseStartTimeRef = useRef<number | null>(null); // Track when user starts speaking
  const isIntroMessageRef = useRef<boolean>(false); // Track if this is an intro message

  // Helper function to get custom headers for public users
  const getCustomHeaders = (): Record<string, string> => {
    const anonId = localStorage.getItem("anon_Id");
    if (anonId) {
      return {
        "X-Anon-ID": anonId,
      };
    }
    return {};
  };

  // Create an array for audio visualization bars
  const bars = Array.from({ length: 12 }, (_, i) => i);

  useEffect(() => {
    initializeVoiceMode();
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (sessionRef.current) {
      // Disconnect the realtime session and stop any audio output
      try {
        sessionRef.current.close();
      } catch (error) {
        console.warn("Error closing realtime session:", error);
      }
    }
    // Reset the response timer and intro message flag
    responseStartTimeRef.current = null;
    isIntroMessageRef.current = false;
  };

  const handleRealtimeEvent = (event: any) => {
    // Log all events to see what's available
    // console.log(`Event: ${event.type}`, event);

    // Handle specific event types from the Realtime API
    switch (event.type) {
      // Fired when the AI truly finishes speaking
      case "output_audio_buffer.stopped":
        setAIIsSpeaking(false);
        // We mute when the AI gives its intro message
        // so we need to unmute when the AI finishes speaking
        if (sessionRef.current?.muted) {
          sessionRef.current.mute(false);
        }
        break;

      // Fired when the user starts speaking
      case "input_audio_buffer.speech_started":
        setUserIsSpeaking(true);
        if (onUserTranscriptionDelta) {
          // This causes the three blinking eclipises to show
          // when the user starts speaking
          onUserTranscriptionDelta(" ");
        }
        break;

      // Fired when the user is speaking
      case "conversation.item.input_audio_transcription.delta":
        // Stream what the user is saying to the Index chatbot component
        setUserIsSpeaking(true);

        if (event.delta && onUserTranscriptionDelta) {
          userTranscriptionRef.current += event.delta;
          onUserTranscriptionDelta(userTranscriptionRef.current);
        }
        break;

      // Fired when the user finishes speaking
      case "conversation.item.input_audio_transcription.completed":
        setUserIsSpeaking(false);
        // Start the response timer when user finishes speaking and question is sent to server
        responseStartTimeRef.current = Date.now();
        // User finished speaking - send complete transcription
        if (onUserTranscriptionComplete && userTranscriptionRef.current) {
          onUserTranscriptionComplete(userTranscriptionRef.current);
          // Only store user message if it's not empty and not just whitespace
          if (userTranscriptionRef.current.trim() !== "") {
            currentUserMessageRef.current = userTranscriptionRef.current; // Store for API call
          }
          userTranscriptionRef.current = ""; // Reset for next utterance
        }
        break;

      // Similiar to the above, also fired when the user
      // stops speaking
      case "input_audio_buffer.speech_stopped":
        setUserIsSpeaking(false);
        break;

      // Next set of cases all deal with the AI Speaking
      case "response.audio_transcript.delta":
        // Stream what the AI is speaking to the Index chatbot component
        setAIIsSpeaking(true);

        if (event.delta && onAIResponseDelta) {
          aiResponseRef.current += event.delta;
          onAIResponseDelta(aiResponseRef.current);
        }
        break;

      case "response.audio_transcript.done":
        // AI finished speaking - send complete response
        if (onAIResponseComplete && aiResponseRef.current) {
          onAIResponseComplete(aiResponseRef.current);
        }
        break;

      case "response.done":
        // AI response completed with token usage information
        console.log(
          "response.done - ThreadId:",
          threadId,
          "Valid:",
          !!(threadId && threadId.trim() !== ""),
        );

        // Skip database record creation if this is an intro message
        if (isIntroMessageRef.current) {
          console.log("Skipping API call - this is an intro message");
          isIntroMessageRef.current = false; // Reset flag for next interaction
          break;
        }

        if (
          threadId &&
          threadId.trim() !== "" &&
          currentUserMessageRef.current &&
          currentUserMessageRef.current.trim() !== ""
        ) {
          const callUpdateInteraction = async () => {
            try {
              // Calculate response time in milliseconds
              const responseTime = responseStartTimeRef.current
                ? Date.now() - responseStartTimeRef.current
                : null;

              // Extract token usage from the response event
              const tokenUsage = event.response?.usage || {};
              const promptTokens = tokenUsage.input_tokens || 0;
              const completionTokens = tokenUsage.output_tokens || 0;
              const totalTokens =
                (tokenUsage.input_tokens || 0) +
                (tokenUsage.output_tokens || 0);

              const requestBody = {
                userMessage: currentUserMessageRef.current,
                aiResponse: aiResponseRef.current,
                threadId: threadId,
                responseTime: responseTime, // Add response time to the API call
                promptTokens: promptTokens,
                completionTokens: completionTokens,
                totalTokens: totalTokens,
              };

              console.log("Sending to UpdateInteraction API:", requestBody);

              const endpoint = isPublic
                ? "ComplianceGuide/UpdateInteractionPublic"
                : "ComplianceGuide/UpdateInteraction";

              const response = await callApiWithOptions({
                endpoint,
                method: HttpMethod.POST,
                body: requestBody,
                customHeaders: isPublic ? getCustomHeaders() : undefined,
              });

              if (!response.success) {
                console.warn("Failed to update interaction:", response.message);
              } else {
                console.log(
                  `Voice interaction completed in ${responseTime}ms with ${totalTokens} total tokens (${promptTokens} prompt, ${completionTokens} completion)`,
                );
                console.log("Token Usage Details:", {
                  inputTokens: promptTokens,
                  outputTokens: completionTokens,
                  totalTokens: totalTokens,
                  responseTime: responseTime,
                });
              }
            } catch (error) {
              console.error("Error updating interaction:", error);
            } finally {
              // Reset for next interaction
              aiResponseRef.current = ""; // Reset for next response
              responseStartTimeRef.current = null;
            }
          };

          callUpdateInteraction();
        } else {
          if (
            !currentUserMessageRef.current ||
            currentUserMessageRef.current.trim() === ""
          ) {
            console.log(
              "Skipping API call - no user question (intro message only)",
            );
          } else {
            console.log("Skipping API call - no valid threadId");
          }
        }

        break;

      case "response.audio.done":
        break;

      default:
        // Abhi: Commenting out for now to reduce noise in console
        //console.log(`Unhandled event type: ${event.type}`, event);
        break;
    }
  };

  const generateEphemeralToken = async (
    voice: string = selectedVoice,
    speed: number = 1.0,
    persona: string = "Neutral",
  ): Promise<string> => {
    const endpoint = isPublic
      ? "ComplianceGuide/GenerateEphemeralTokenPublic"
      : "ComplianceGuide/GenerateEphemeralToken";

    const response = (await callApiWithOptions({
      endpoint,
      method: HttpMethod.POST,
      body: {
        voice,
        speed,
        persona,
      },
      customHeaders: isPublic ? getCustomHeaders() : undefined,
    })) as EphemeralTokenResponse;

    if (!response.token) {
      throw new Error(response.error || "Failed to generate token");
    }

    return response.token;
  };

  // Otherwise I get two voice agents in dev because of strict mode - Jeff
  const hasCreated = React.useRef(false);

  // RAG Search Tool - searches the Azure AI Search knowledge base
  const searchKnowledgeBaseTool = tool({
    name: "search_knowledge_base",
    description:
      "Search the compliance knowledge base for relevant information about DNC (Do Not Call), TCPA, telemarketing regulations, and compliance topics. Use this tool when the user asks questions about compliance rules, regulations, or best practices.",
    parameters: z.object({
      query: z
        .string()
        .describe(
          "The search query to find relevant compliance information. Be specific and include key terms from the user's question.",
        ),
    }),
    execute: async ({ query }) => {
      try {
        const endpoint = isPublic
          ? "ComplianceGuide/SearchKnowledgeBasePublic"
          : "ComplianceGuide/SearchKnowledgeBase";

        const response = await callApiWithOptions({
          endpoint,
          method: HttpMethod.POST,
          body: { query },
          customHeaders: isPublic ? getCustomHeaders() : undefined,
        });

        if (response.success && response.content) {
          return response.content;
        }

        return "No relevant information found in the knowledge base for this query.";
      } catch (error) {
        console.error("Error searching knowledge base:", error);
        return "Unable to search the knowledge base at this time.";
      }
    },
  });

  const initializeVoiceMode = async (): Promise<void> => {
    if (hasCreated.current) return;
    hasCreated.current = true;

    try {
      setIsConnecting(true);
      setConnectionStatus("Establishing voice channel...");
      setError(null);

      // Generate ephemeral token with selected persona
      const personaName = selectedPersona === "donna" ? "Donna" : "Neutral";
      const apiKey = await generateEphemeralToken(
        selectedVoice,
        1.0,
        personaName,
      );

      setConnectionStatus("Creating AI agent...");

      // Create the realtime agent with compliance assistant configuration
      const agent = new RealtimeAgent({
        name: "AI Compliance Assistant",
        tools: [searchKnowledgeBaseTool],
        voice: selectedVoice,
      });

      setConnectionStatus("Establishing voice connection...");

      // Create session
      // The ephmeral token used for authentication
      // sets I believe almost all options
      // I don't think what is passed in here
      // overrides the settings
      const session = new RealtimeSession(agent);

      sessionRef.current = session;

      // Set up event listeners
      session.on("transport_event", (event: any) => {
        // this logs the events coming directly from the Realtime API server
        // Abhi: Commenting out for now to reduce noise in console
        // console.log("Transport event:", event);

        // Dispatch to specific event handlers based on event type
        handleRealtimeEvent(event);
      });

      // Handle error events
      session.on("error", (event: any) => {
        console.error("Session error:", event);
      });

      // Connect to the session
      await session.connect({ apiKey });

      // Send intro message and mark it as such to prevent database record creation
      isIntroMessageRef.current = true;
      session.sendMessage(
        `Introduce yourself. The user will only speak to you in English. Don't let you turn be interrupted unless you think the user is talking a full sentence.`,
      );
      session.mute(true);

      setIsConnected(true);
      setIsConnecting(false);
      setConnectionStatus("Connected - Start speaking!");

      // Start audio visualization
      startAudioVisualization();
    } catch (error: any) {
      console.error("Error initializing voice mode:", error);
      setError(error.message || "Failed to initialize voice mode");
      setIsConnecting(false);
      setConnectionStatus("Connection failed");
    }
  };

  const startAudioVisualization = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw animated bars
      bars.forEach((_, index) => {
        const barWidth = 8;
        const barSpacing = 12;
        const x = index * barSpacing + 10;

        // Initial implementation by AI was that the height
        // of the bars changed based on teh state of speaking but I found
        // it better if only color changed by the state of speaking
        const baseHeight = 10;
        const animatedHeight =
          baseHeight + Math.sin(Date.now() * 0.005 + index * 0.2) * 5;

        const y = canvas.height - animatedHeight;

        // Color based on state
        let color = "#007bff"; // Default blue
        if (userIsSpeakingRef.current) {
          color = "#28a745"; // Green when user is speaking
        } else if (aiIsSpeakingRef.current) {
          color = "#007bff"; // Blue when AI is speaking
        } else if (isConnectedRef.current) {
          color = "#17a2b8"; // Teal when ready
        }

        ctx.fillStyle = color;
        ctx.fillRect(x, y, barWidth, animatedHeight);

        // Add glow effect
        ctx.shadowColor = color;
        ctx.shadowBlur = 10;
        ctx.fillRect(x, y, barWidth, animatedHeight);
        ctx.shadowBlur = 0;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const handleClose = () => {
    // Reset states immediately for user feedback
    setIsConnected(false);
    setUserIsSpeaking(false);
    setAIIsSpeaking(false);
    setConnectionStatus("Disconnected");

    // Clean up resources and close session
    cleanup();
    onClose();
  };

  return (
    <div className={styles.inlineContainer}>
      {error ? (
        <div className={styles.error}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          <span className="small">{error}</span>
          <button
            className="btn btn-outline-primary btn-sm ms-2"
            onClick={initializeVoiceMode}
            disabled={isConnecting}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className={styles.voiceContent}>
          <div>
            <canvas
              ref={canvasRef}
              width={120}
              height={40}
              className={styles.canvas}
            />
          </div>

          <div className={styles.statusBadge}>
            <div
              className={`${styles.statusIndicator} ${isConnected ? styles.connected : isConnecting ? styles.connecting : styles.disconnected}`}
            >
              {isConnecting && <i className="bi bi-hourglass-split me-1"></i>}
              {isConnected && !userIsSpeaking && !aiIsSpeaking && (
                // Display empty microphone icon when not speaking
                <i className="bi bi-mic me-1"></i>
              )}
              {userIsSpeaking && (
                // Display filled microphone icon when user is speaking
                <i className="bi bi-mic-fill me-1 text-success"></i>
              )}
              {aiIsSpeaking && (
                // Display filled volume icon when AI is speaking
                <i className="bi bi-volume-up me-1 text-primary"></i>
              )}
              {!isConnected && !isConnecting && (
                <i className="bi bi-x-circle me-1 text-danger"></i>
              )}

              <span className="small">{connectionStatus}</span>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary btn-sm ms-2"
            onClick={handleClose}
            title="Close Voice Mode"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default VoiceMode;
